import React, { useState, ChangeEvent } from 'react';
import '../styles/intake.css';
import { PageType, SimulationData } from '../App';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';

interface IntakePageProps {
  setCurrentPage: (page: PageType) => void;
  initialData?: SimulationData;
}

interface FormData {
  country: string;
  city: string;
  company: string;
  website: string;
  timeline: string;
  hires: string;
  positions: string[];
  workspace: string;
  fullName: string;
  email: string;
  hqCountry: string;
  position: string;
  phone: string;
  aov: string;
  aovRange: string;
  salesCycle: string;
  deliveryCost: string;
  deliveryCostRange: string;
  budget: string;
  budgetRange: string;
  entity: string;
}

const POSITION_OPTIONS: string[] = [
  'Country Manager', 'Regional Manager', 'Project Manager', 'Business Developer',
  'Sales Manager', 'Marketing Manager', 'Operations Lead', 'Finance / Admin',
  'Technical Lead', 'Customer Success'
];

const TIMELINE_OPTIONS = [
  { v: '0-3', l: '0–3', c: 'Fast track — immediate need' },
  { v: '3-6', l: '3–6', c: 'Standard — time to prepare' },
  { v: '6-12', l: '6–12', c: 'Strategic — phased rollout' },
  { v: '12-18', l: '12–18', c: 'Long-term — full evaluation' }
];

const WORKSPACE_OPTIONS = [
  { v: 'office', n: 'Office', d: 'Private office or co-working space. Full physical presence.' },
  { v: 'remote', n: 'Remote', d: 'Fully remote team. Lower overhead, wider talent pool.' },
  { v: 'hybrid', n: 'Hybrid', d: 'Flexible — co-working days + remote. Best of both worlds.' }
];

const IntakePage: React.FC<IntakePageProps> = ({ setCurrentPage, initialData = {} }) => {
  const [step, setStep] = useState<number>(1);
  const [formData, setFormData] = useState<FormData>({
    country: initialData.country ?? '',
    city: initialData.city ?? '',
    company: '',
    website: '',
    timeline: '3-6',
    hires: '',
    positions: [],
    workspace: 'office',
    fullName: '',
    email: (initialData.email as string) ?? '',
    hqCountry: '',
    position: '',
    phone: '',
    aov: '',
    aovRange: '',
    salesCycle: '',
    deliveryCost: '',
    deliveryCostRange: '',
    budget: '',
    budgetRange: '',
    entity: ''
  });
  const [submitted, setSubmitted] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [submitError, setSubmitError] = useState<string>('');

  const update = (field: keyof FormData, value: string | string[]): void => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const togglePosition = (pos: string): void => {
    update('positions', formData.positions.includes(pos)
      ? formData.positions.filter(p => p !== pos)
      : [...formData.positions, pos]);
  };

  const handleSubmit = async (): Promise<void> => {
    setSubmitError('');
    setSubmitting(true);
    try {
      await addDoc(collection(db, 'leads'), {
        targetCountry: formData.country,
        targetCity: formData.city,
        companyName: formData.company,
        website: formData.website,
        expansionTimeline: formData.timeline,
        hires: formData.hires,
        positions: formData.positions,
        workspace: formData.workspace,
        fullName: formData.fullName,
        email: formData.email.toLowerCase().trim(),
        hqCountry: formData.hqCountry,
        position: formData.position,
        phone: formData.phone,
        aov: formData.aov,
        aovRange: formData.aovRange,
        salesCycle: formData.salesCycle,
        deliveryCost: formData.deliveryCost,
        deliveryCostRange: formData.deliveryCostRange,
        budget: formData.budget,
        budgetRange: formData.budgetRange,
        entity: formData.entity,
        status: 'pending',
        createdAt: serverTimestamp(),
      });
      setSubmitted(true);
    } catch (err) {
      console.error('Error submitting lead:', err);
      setSubmitError('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="intake-wrap">
        <div className="intake-inner">
          <div className="intake-success show">
            <div className="intake-success-check">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
            </div>
            <div className="intake-success-eyebrow">Request confirmed</div>
            <h2>We're building your<br /><em>Market Entry Simulation.</em></h2>
            <p className="intake-success-sub">Your details are in. Our team is now working on your simulation — every number modelled on your target market, your business, and your expansion plan.</p>
            <div className="intake-success-timer">
              <div className="intake-success-timer-icon">
                <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.65)" strokeWidth="1.8" strokeLinecap="round"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>
              </div>
              <div className="intake-success-timer-text">
                <div className="intake-success-timer-label">Estimated delivery</div>
                <div className="intake-success-timer-val">Within 72 hours</div>
              </div>
            </div>
            <div style={{ maxWidth: '460px', margin: '0 auto 6px', textAlign: 'left' }}>
              <div style={{ fontSize: '10px', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--sage)', marginBottom: '4px' }}>What happens next</div>
            </div>
            <div className="intake-success-steps">
              {[
                { done: true, title: 'Simulation request submitted', desc: 'Your expansion details have been received. We have everything we need to begin.' },
                { done: false, title: 'We build your simulation', desc: 'Our team sources real local benchmarks including salaries, employer taxes, setup costs, and acquisition data, then models your full 12-month entry from scratch.' },
                { done: false, title: 'You receive your simulation', desc: 'A complete market entry simulation with go/no-go verdict, month-by-month cashflow, full cost breakdown, and multi-market comparison. Ready to make your decision.' }
              ].map((s, i) => (
                <div key={i} className="intake-success-step">
                  <div className={`is-step-num ${s.done ? 'done' : ''}`}>
                    {s.done
                      ? <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3.5" strokeLinecap="round"><polyline points="20 6 9 17 4 12" /></svg>
                      : i + 1
                    }
                  </div>
                  <div className="is-step-body">
                    <div className="is-step-title">{s.title}</div>
                    <div className="is-step-desc">{s.desc}</div>
                  </div>
                </div>
              ))}
            </div>
            <div className="intake-success-notify">
              <div className="intake-success-notify-icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#7c9a6e" strokeWidth="2" strokeLinecap="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.73 21a2 2 0 0 1-3.46 0" /></svg>
              </div>
              <div className="intake-success-notify-text">
                <strong>You'll be notified in two places.</strong> We'll send you a notification to your email and inside the platform the moment your simulation is ready.
              </div>
            </div>
            <div style={{ maxWidth: '460px', margin: '24px auto 0', padding: '20px 0 0', textAlign: 'center', borderTop: '1px solid var(--cream-dark)' }}>
              <button className="ibtn-next" onClick={() => setCurrentPage('home')}>Back to home</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="intake-wrap">
      <div className="intake-inner">
        <button className="intake-back" onClick={() => setCurrentPage('home')}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M19 12H5M12 19l-7-7 7-7" /></svg>
          Back to home
        </button>

        <div className="intake-header">
          <h1>New Simulation.</h1>
          <p>Tell us about your expansion plan. <em>We keep everything strictly confidential.</em></p>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '10px', marginTop: '18px', padding: '12px 16px', background: '#f5f1ea', borderRadius: '10px', border: '1px solid #ddd8ce', maxWidth: '520px' }}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#7c9a6e" strokeWidth="2" strokeLinecap="round" style={{ flexShrink: 0 }}><rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></svg>
            <p style={{ fontSize: '12px', color: '#7a8a7e', lineHeight: '1.5', margin: '0' }}><strong style={{ color: '#1a2e1f', fontWeight: 600 }}>Your data stays private.</strong> All information you share remains strictly confidential and is never shared with any third party without your explicit permission.</p>
          </div>
        </div>

        {/* Step indicators */}
        <div className="intake-steps">
          {['Market & Team', 'About You', 'Details & Submit'].map((label, i) => {
            const n = i + 1;
            const cls = step === n ? 's-active' : step > n ? 's-done' : 's-inactive';
            return (
              <React.Fragment key={i}>
                {i > 0 && <div className="intake-step-line"></div>}
                <div className={`intake-step-ind ${cls}`}>
                  <div className="intake-step-num">{n}</div>
                  <div className="intake-step-lbl">{label}</div>
                </div>
              </React.Fragment>
            );
          })}
        </div>

        {/* Step 1 */}
        {step === 1 && (
          <div className="intake-panel active">
            <div className="intake-sec-lbl">Target Market</div>
            <div className="intake-frow">
              <div className="intake-field">
                <label>Target Country {!initialData.country && '*'}</label>
                {initialData.country
                  ? <div className="intake-readonly">{formData.country}</div>
                  : (
                    <select value={formData.country} onChange={(e: ChangeEvent<HTMLSelectElement>) => update('country', e.target.value)} required>
                      <option value="">Select a Country</option>
                      {['United States','United Kingdom','Germany','France','Netherlands','Spain','Italy','Canada','Australia','Japan','UAE','Singapore','Sweden','Denmark','Poland'].map(c => <option key={c}>{c}</option>)}
                    </select>
                  )
                }
              </div>
              <div className="intake-field">
                <label>Target City {!initialData.city && '*'}</label>
                {initialData.city
                  ? <div className="intake-readonly">{formData.city}</div>
                  : <input type="text" value={formData.city} onChange={(e: ChangeEvent<HTMLInputElement>) => update('city', e.target.value)} placeholder="e.g., Berlin" required />
                }
              </div>
            </div>

            <div className="intake-sec-lbl">Your Company</div>
            <div className="intake-frow">
              <div className="intake-field">
                <label>Company Name *</label>
                <input type="text" value={formData.company} onChange={(e: ChangeEvent<HTMLInputElement>) => update('company', e.target.value)} placeholder="e.g., Acme Inc." required />
              </div>
              <div className="intake-field">
                <label>Company Website</label>
                <input type="url" value={formData.website} onChange={(e: ChangeEvent<HTMLInputElement>) => update('website', e.target.value)} placeholder="e.g., https://acme.com" />
              </div>
            </div>

            <div className="intake-sec-lbl">Timeline — When do you want to be operational?</div>
            <div className="intake-timeline-grid">
              {TIMELINE_OPTIONS.map(t => (
                <div key={t.v} className={`intake-tile ${formData.timeline === t.v ? 'sel' : ''}`} onClick={() => update('timeline', t.v)}>
                  <div className="tile-radio"></div>
                  <div className="tile-big">{t.l}</div>
                  <div className="tile-unit">Months</div>
                  <div className="tile-caption">{t.c}</div>
                </div>
              ))}
            </div>

            <div className="intake-sec-lbl">Team — Positions & Headcount</div>
            <div className="intake-frow single">
              <div className="intake-field">
                <label>Number of hires over first 12 months *</label>
                <input type="number" value={formData.hires} onChange={(e: ChangeEvent<HTMLInputElement>) => update('hires', e.target.value)} placeholder="e.g., 5" min="1" required />
              </div>
            </div>
            <div style={{ fontSize: '10px', fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--text-mid)', marginBottom: '10px' }}>Positions — select all that apply</div>
            <div className="intake-chips">
              {POSITION_OPTIONS.map(pos => (
                <div key={pos} className={`pos-chip ${formData.positions.includes(pos) ? 'sel' : ''}`} onClick={() => togglePosition(pos)}>{pos}</div>
              ))}
            </div>

            <div className="intake-sec-lbl">Workspace Setup</div>
            <div className="intake-ws-grid">
              {WORKSPACE_OPTIONS.map(w => (
                <div key={w.v} className={`ws-tile ${formData.workspace === w.v ? 'sel' : ''}`} onClick={() => update('workspace', w.v)}>
                  <div className="tile-radio"></div>
                  <div className="ws-name">{w.n}</div>
                  <div className="ws-desc">{w.d}</div>
                </div>
              ))}
            </div>

            <div className="intake-nav" style={{ justifyContent: 'flex-end' }}>
              <button className="ibtn-next" onClick={() => setStep(2)}>Continue to Step 2 →</button>
            </div>
          </div>
        )}

        {/* Step 2 */}
        {step === 2 && (
          <div className="intake-panel active">
            <div className="intake-sec-lbl">About You</div>
            <div className="intake-frow">
              <div className="intake-field">
                <label>First & Last Name *</label>
                <input type="text" value={formData.fullName} onChange={(e: ChangeEvent<HTMLInputElement>) => update('fullName', e.target.value)} placeholder="e.g., John Doe" required />
              </div>
              <div className="intake-field">
                <label>Current HQ Country</label>
                <input type="text" value={formData.hqCountry} onChange={(e: ChangeEvent<HTMLInputElement>) => update('hqCountry', e.target.value)} placeholder="e.g., Sweden" />
              </div>
            </div>
            <div className="intake-frow">
              <div className="intake-field">
                <label>Your Position *</label>
                <input type="text" value={formData.position} onChange={(e: ChangeEvent<HTMLInputElement>) => update('position', e.target.value)} placeholder="e.g., CEO, Head of Expansion" required />
              </div>
              <div className="intake-field">
                <label>Work Email *</label>
                <input type="email" value={formData.email} onChange={(e: ChangeEvent<HTMLInputElement>) => update('email', e.target.value)} placeholder="e.g., you@company.com" required />
              </div>
            </div>
            <div className="intake-frow">
              <div className="intake-field">
                <label>Phone Number</label>
                <input type="tel" value={formData.phone} onChange={(e: ChangeEvent<HTMLInputElement>) => update('phone', e.target.value)} placeholder="e.g., +46 70 123 45 67" />
              </div>
              <div className="intake-field"></div>
            </div>
            <div className="intake-nav" style={{ justifyContent: 'space-between' }}>
              <button className="ibtn-back" onClick={() => setStep(1)}>← Back</button>
              <button className="ibtn-next" onClick={() => setStep(3)}>Continue to Step 3 →</button>
            </div>
          </div>
        )}

        {/* Step 3 */}
        {step === 3 && (
          <div className="intake-panel active">
            <div className="intake-sec-lbl">Business Details</div>
            <div className="intake-frow">
              <div className="intake-field">
                <label>Average Sale Value</label>
                <div style={{ position: 'relative' }}>
                  <span style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', fontSize: '14px', color: '#4a5a4e', pointerEvents: 'none' }}>€</span>
                  <input type="number" value={formData.aov} onChange={(e: ChangeEvent<HTMLInputElement>) => update('aov', e.target.value)} placeholder="e.g., 3500" min="0" style={{ paddingLeft: '28px' }} />
                </div>
                <select value={formData.aovRange} onChange={(e: ChangeEvent<HTMLSelectElement>) => {
                  const val = e.target.value;
                  if (val) { const match = val.match(/[\d,]+/g); update('aov', match ? match[0].replace(/,/g, '') : ''); }
                  update('aovRange', val);
                }} style={{ marginTop: '6px' }}>
                  <option value="">or pick a range</option>
                  {['Under €500','€500 – €1,000','€1,000 – €2,500','€2,500 – €5,000','€5,000 – €10,000','€10,000 – €25,000','€25,000 – €50,000','€50,000 – €100,000','€100,000 – €250,000','Over €250,000'].map(o => <option key={o}>{o}</option>)}
                </select>
                <p className="intake-hint" style={{ marginTop: '6px' }}>The average value of a single closed deal.</p>
              </div>
              <div className="intake-field">
                <label>Sales Cycle Length</label>
                <select value={formData.salesCycle} onChange={(e: ChangeEvent<HTMLSelectElement>) => update('salesCycle', e.target.value)}>
                  <option value="">Select a range</option>
                  {['Under 1 week','1 – 2 weeks','2 – 4 weeks','1 – 2 months','2 – 3 months','3 – 6 months','6 – 12 months','Over 12 months'].map(o => <option key={o}>{o}</option>)}
                </select>
                <p className="intake-hint" style={{ marginTop: '6px' }}>How long it typically takes you to close a new customer.</p>
              </div>
            </div>
            <div className="intake-frow">
              <div className="intake-field">
                <label>Cost of Fulfilling a Sale</label>
                <div style={{ position: 'relative' }}>
                  <input type="number" value={formData.deliveryCost} onChange={(e: ChangeEvent<HTMLInputElement>) => update('deliveryCost', e.target.value)} placeholder="e.g., 30" min="10" max="100" style={{ paddingRight: '32px' }} />
                  <span style={{ position: 'absolute', right: '14px', top: '50%', transform: 'translateY(-50%)', fontSize: '14px', color: '#4a5a4e', pointerEvents: 'none' }}>%</span>
                </div>
                <select value={formData.deliveryCostRange} onChange={(e: ChangeEvent<HTMLSelectElement>) => {
                  const val = e.target.value;
                  if (val) { const match = val.match(/\d+/); update('deliveryCost', match ? match[0] : ''); }
                  update('deliveryCostRange', val);
                }} style={{ marginTop: '6px' }}>
                  <option value="">or pick a range</option>
                  {['10% – 20%','20% – 30%','30% – 40%','40% – 50%','50% – 60%','60% – 70%','70% – 80%','Over 80%'].map(o => <option key={o}>{o}</option>)}
                </select>
                <p className="intake-hint" style={{ marginTop: '6px' }}>What it costs you to deliver one closed deal.</p>
              </div>
              <div className="intake-field">
                <label>Expansion Budget</label>
                <div style={{ position: 'relative' }}>
                  <span style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', fontSize: '14px', color: '#4a5a4e', pointerEvents: 'none' }}>€</span>
                  <input type="number" value={formData.budget} onChange={(e: ChangeEvent<HTMLInputElement>) => update('budget', e.target.value)} placeholder="e.g., 75000" min="0" style={{ paddingLeft: '28px' }} />
                </div>
                <select value={formData.budgetRange} onChange={(e: ChangeEvent<HTMLSelectElement>) => {
                  const val = e.target.value;
                  if (val) { const match = val.match(/[\d,]+/g); update('budget', match ? match[0].replace(/,/g, '') : ''); }
                  update('budgetRange', val);
                }} style={{ marginTop: '6px' }}>
                  <option value="">or pick a range</option>
                  {['Under €25,000','€25,000 – €50,000','€50,000 – €75,000','€75,000 – €100,000','€100,000 – €150,000','€150,000 – €250,000','€250,000 – €500,000','€500,000 – €750,000','€750,000 – €1M','Over €1M'].map(o => <option key={o}>{o}</option>)}
                </select>
                <p className="intake-hint" style={{ marginTop: '6px' }}>Total budget allocated for entering the new market.</p>
              </div>
            </div>
            <div className="intake-frow single">
              <div className="intake-field">
                <label>Preferred Entity Structure</label>
                <select value={formData.entity} onChange={(e: ChangeEvent<HTMLSelectElement>) => update('entity', e.target.value)}>
                  <option value="">Select an option</option>
                  <option>EOR (Employer of Record) — faster setup, lower cost</option>
                  <option>Local LLC / Subsidiary — full legal presence</option>
                  <option>Branch Office — extension of parent company</option>
                  <option>Partnership / Joint Venture — with local partner</option>
                  <option>Not sure yet</option>
                </select>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', marginTop: '8px', marginBottom: '4px', padding: '13px 16px', background: '#fffbf4', borderRadius: '10px', border: '1px solid #e8dfc8' }}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#c4a85a" strokeWidth="2" strokeLinecap="round" style={{ flexShrink: 0, marginTop: '1px' }}><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>
              <p style={{ fontSize: '12px', color: '#7a6a3a', lineHeight: '1.6', margin: '0' }}>
                <strong style={{ color: '#5a4a1a', fontWeight: 600 }}>Exact values give sharper results.</strong> If you select a range, we'll calculate using the midpoint. Enter your actual figures above for the most realistic output.
              </p>
            </div>
            {submitError && <p style={{ color: '#c0392b', fontSize: '13px', marginBottom: '8px' }}>{submitError}</p>}
            <div className="intake-nav" style={{ justifyContent: 'space-between' }}>
              <button className="ibtn-back" onClick={() => setStep(2)}>← Back</button>
              <button className="ibtn-next" onClick={handleSubmit} disabled={submitting}>
                {submitting ? 'Submitting...' : (
                  <>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="20 6 9 17 4 12" /></svg>
                    Get My Free Simulation
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default IntakePage;
